import { Buffer } from 'buffer';
import { Box, Typography, alpha } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { makeStyles } from 'tss-react/mui';

import { useQueryClient } from '@tanstack/react-query';
import PickImage from '../../../../components/FormInputs/PickMedia';
import useWidth from '../../../../components/Hooks/useWidth';
import useMediaStyles from '../../../../components/useMediaStyles';
import LangEnum from '../../../../lang/LangEnum';
import { IMedia, getMediaCover, readImageAsBase64 } from '../../../../lib/MediasLib';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { imageMimeTypes } from '../../../media/mimeType';
import { useTranslation } from '../../../translation/TranslationContext';
import { validateOrderConditionsQuery } from '../../contexts/order.utils';
import { useSignatureContext } from '../contexts/signature.context';

interface UploadTabPanelProps {
  orderId: string;
}

const useStyles = makeStyles()((theme) => ({
  addImageContainer: {
    height: '35vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.custom.border.primary, 0.5)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    [theme.breakpoints.only('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.only('sm')]: {
      width: '60vw',
    },
    [theme.breakpoints.only('md')]: {
      width: '65vw',
    },
    [theme.breakpoints.only('lg')]: {
      width: '50vw',
    },
    [theme.breakpoints.up('xl')]: {
      width: '35vw',
    },
  },
  dropzone: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  dragHover: {
    border: `4px dashed ${theme.palette.custom.border.primary}`,
  },
  clickable: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  wrapper: {
    width: '100%',
    height: '35vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
  },
}));

const size5MB = 5000000;

const UploadTabPanel = ({ orderId }: UploadTabPanelProps) => {
  const { changeCleanSign, changeHasSignInput, changeHandleGenerateSignedContractPdf } =
    useSignatureContext();
  const [drag, setDrag] = useState<boolean>(false);
  const [sign, setSign] = useState<Buffer | null>(null);
  const { classes: mediaClasses } = useMediaStyles({});
  const { classes, cx } = useStyles();
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const [filesUrl, setfilesUrl] = useState<IMedia[]>([]);
  const { mediumDownWidth } = useWidth();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const refetchValidateConditionsQuery = useCallback(() => {
    queryClient.refetchQueries(validateOrderConditionsQuery(orderId, team?.id));
  }, [queryClient, orderId, team?.id]);

  const addAndProccessWithLimit = async (files: File[]) => {
    if (files[0].size > size5MB) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.ORDER_CONTRACT_TAB_DIGIT_WARNING_LIMIT_SIZE),
      });
      return;
    }
    if (!imageMimeTypes.includes(files[0].type)) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.ORDER_CONTRACT_TAB_DIGIT_WARNING_FORMAT),
      });
      return;
    }
    const newfilesUrl: IMedia[] = [];
    const fb64 = await readImageAsBase64(files[0]);
    newfilesUrl.push({
      file: files[0],
      coverUrl: fb64 as string,
      url: '',
      position: 0,
      type: 'IMAGE',
    });

    setfilesUrl(newfilesUrl);

    const reader = new FileReader();
    reader.readAsArrayBuffer(files[0]);

    reader.onload = () => {
      setSign(Buffer.from(reader.result!));
    };
    changeHasSignInput(true);
  };

  useEffect(() => {
    changeHandleGenerateSignedContractPdf(() => sign!, refetchValidateConditionsQuery);
    changeCleanSign(() => {
      setSign(null);
      setfilesUrl([]);
    });
  }, [
    sign,
    changeCleanSign,
    refetchValidateConditionsQuery,
    changeHandleGenerateSignedContractPdf,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <div className={classes.wrapper}>
        {filesUrl.length > 0 ? (
          filesUrl.map((fileB64, index) => (
            <img
              key={`file-${index}`}
              alt={fileB64?.file?.name ?? `file-${index}`}
              src={getMediaCover(fileB64)}
            />
          ))
        ) : (
          <Dropzone
            onDropAccepted={addAndProccessWithLimit}
            onDrop={() => setDrag(false)}
            onDragEnter={() => setDrag(true)}
            onDragLeave={() => setDrag(false)}
          >
            {({ getRootProps }) => (
              <PickImage id="signatureImage" onChange={addAndProccessWithLimit} multiple>
                <div
                  {...getRootProps()}
                  className={cx(mediaClasses.root, classes.addImageContainer)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={cx(classes.dropzone, drag ? classes.dragHover : '')}>
                    <Typography className={classes.clickable}>
                      {mediumDownWidth
                        ? 'adicionar assinatura com fundo transparente (.png)'
                        : translate(LangEnum.ORDER_CONTRACT_UPLOAD_SIGNATURE)}
                    </Typography>
                  </div>
                </div>
              </PickImage>
            )}
          </Dropzone>
        )}
      </div>
    </Box>
  );
};

export default UploadTabPanel;
